import React from "react"
import styled, { css } from "styled-components"
import {
  space,
  overflow,
  size,
  height,
  minHeight,
  left,
  right,
  top,
  bottom,
  width,
  alignSelf,
  AlignSelfProps,
  justifyContent,
  flexDirection,
  alignItems,
  JustifyContentProps,
  AlignItemsProps,
  SpaceProps,
  FlexDirectionProps,
  OverflowProps,
  textAlign,
  HeightProps,
  MinHeightProps,
  TextAlignProps,
  WidthProps,
  MaxWidthProps,
  maxWidth,
  minWidth,
  MinWidthProps,
  VerticalAlignProps,
  verticalAlign,
  border,
  borderTop,
  BorderTopProps,
  borderWidth,
  BorderWidthProps,
  BordersProps,
  borderColor,
  color,
  DisplayProps,
  display,
  FlexProps,
  flex,
  flexBasis,
  FlexBasisProps,
  FlexWrapProps,
  flexWrap,
  LeftProps,
  RightProps,
  BottomProps,
  TopProps,
  borderStyle,
  BoxShadowProps,
  boxShadow,
  position,
  PositionProps,
  zIndex,
  ZIndexProps,
  OrderProps,
  order
} from "styled-system"
import { flexGrow, FlexGrowProps } from "theme/custom-system-parts"
import { layer, ColorProps } from "theme"
import { breakpoints } from "components/helpers/hide"

export type BoxProps = {
  as?: any
  layer?: "top" | "middle"
} & SpaceProps &
  OverflowProps &
  TextAlignProps &
  WidthProps &
  HeightProps &
  MinHeightProps &
  MaxWidthProps &
  VerticalAlignProps &
  BordersProps &
  BorderTopProps &
  BorderWidthProps &
  ColorProps &
  DisplayProps &
  LeftProps &
  BottomProps &
  RightProps &
  TopProps &
  BoxShadowProps &
  PositionProps &
  AlignSelfProps &
  MinWidthProps &
  ZIndexProps

export type FlexContainerProps = BoxProps &
  AlignItemsProps &
  FlexDirectionProps &
  JustifyContentProps &
  FlexWrapProps &
  AlignSelfProps

export type FlexItemProps = FlexBasisProps & FlexProps & FlexContainerProps & FlexGrowProps & OrderProps

export const Box = styled.div<BoxProps>`
  box-sizing: border-box;
  ${space}
  ${position}
  ${overflow}
  ${size}
  ${height}
  ${minHeight}
  ${left}
  ${right}
  ${top}
  ${bottom}
  ${textAlign}
  ${width}
  ${minWidth}
  ${maxWidth}
  ${verticalAlign}
  ${border}
  ${borderTop}
  ${borderStyle}
  ${borderWidth}
  ${borderColor}
  ${color}
  ${display}
  ${boxShadow}
  ${alignSelf}
  ${zIndex}
  ${order}
`
type ContentRowProps = BoxProps & {
  children?: React.ReactNode
  /**
   * True to pad the bottom of the content row and adjust subsequent content rows. Useful when trying to make backgrounds of sections work.
   */
  padBottom?: boolean
}

const ContentRowStyled = styled(Box)<ContentRowProps>`
  & + & {
    ${props =>
      props.padBottom &&
      `
      /* Since we padded the bottom of the previous element, pad the top of the subsequent one less */
      padding-top: ${props.theme.space[5]}

      @media (min-width: ${props.theme.breakpoints.md}px) {
        padding-top: ${props.theme.space[7]};
      }

    `}
  }
`

export const ContentRow: React.FunctionComponent<ContentRowProps> = props => (
  <ContentRowStyled pt={[7, 7, 9, 9]} pb={props.padBottom ? [5, 5, 7, 7] : undefined} {...props}>
    {props.children}
  </ContentRowStyled>
)

export const BoxConstrained = styled(Box)``
BoxConstrained.defaultProps = {
  mx: "auto",
  maxWidth: [1, 1, 4, 4]
}

export const InlineBox = styled(Box)`
  display: inline-block;
`

export const InlineList = styled(InlineBox)``
InlineList.defaultProps = {
  as: "ul",
  mx: -4
}

export const InlineListItem = styled(InlineBox)<{ hideMobile?: boolean }>`
  ${props =>
    props.hideMobile &&
    css`
      ${breakpoints.sm} {
        display: none;
      }
      ${breakpoints.md} {
        display: none;
      }
    `}
`

InlineListItem.defaultProps = {
  as: "li",
  px: 4,
  verticalAlign: "middle"
}

export const Flex = styled(Box)<FlexContainerProps>`
  display: flex;
  ${justifyContent}
  ${alignItems}
  ${flexDirection}
  ${flexWrap}
  ${alignSelf}
`

export const FlexColumn = styled(Flex)``
FlexColumn.defaultProps = {
  alignItems: "center",
  flexDirection: "column"
}

export const FlexRow = styled(Flex)``

export const FlexItem = styled(Flex)<FlexItemProps>`
  ${flex}
  ${flexBasis}
  ${flexGrow}
  ${order}
`

FlexRow.defaultProps = {
  alignItems: "center",
  flexDirection: "row"
}

export const StretchBox = styled(Box)<BoxProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${layer}
`

export const RelativeBox = styled(Box)<BoxProps>`
  position: relative;
  ${layer}
`

type AbsoluteBoxProps = {
  /**
   * The anchor of the source element. Default to left.
   */
  anchor?: "top-right" | "bottom-right" | "right" | "bottom" | "center"
} & BoxProps

export const AbsoluteBox = styled(Box)<AbsoluteBoxProps>`
  position: absolute;
  line-height: 0; /* Resetting lineheight because of odd positioning */
  ${layer}
  ${props => {
    switch (props.anchor) {
      case "bottom-right":
        return css`
          bottom: 0;
          right: 0;
        `
        break
      case "top-right":
        return css`
          top: 0;
          right: 0;
        `
      case "right":
        return css`
          right: 0;
        `
      case "bottom":
        return css`
          bottom: 0;
        `
      case "center":
        return css`
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `
      default:
        return css``
    }
  }}
`

export const RoundedBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 5px;
  border-width: 2px;
  border-style: solid;
`
